import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ReasoningApi,
  ReasoningEvidence,
  ReasoningSession,
  ReasoningTraceEvent,
} from "~/api/reasoningTypes";
import { evidenceKey } from "~/features/reasoning/candidateDraft";
import { Button } from "~/foundation/components/Button";

/** The engine's step vocabulary — identifiers, shown as written. */
export const STEP_KINDS = [
  "interpretation",
  "assumption",
  "alternative",
  "decision",
  "action-intent",
  "tool-request",
  "tool-observation",
  "verification",
  "revision",
] as const;
const PICK_LIMIT = 16;

export type StepInput = Omit<
  Parameters<ReasoningApi["step"]>[0],
  "sessionId" | "expectedSessionRevision" | "commandId"
>;

/** An actor's contemporaneous report of an observable step: inputs, causes and pinned evidence, exact. */
export function StepForm({
  session,
  events,
  availableEvidence,
  disabled,
  onRecord,
}: {
  session: ReasoningSession;
  events: ReasoningTraceEvent[];
  availableEvidence: ReasoningEvidence[];
  disabled: boolean;
  /** Resolves true when the step was committed; the form then clears. */
  onRecord: (input: StepInput) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<string>("interpretation");
  const [summary, setSummary] = useState("");
  const [rationale, setRationale] = useState("");
  const [inputs, setInputs] = useState<string[]>([]);
  const [causes, setCauses] = useState<string[]>([]);
  const [evidence, setEvidence] = useState<string[]>([]);
  const toggle = (list: string[], id: string, on: boolean, limit = PICK_LIMIT) =>
    on ? [...list, id].slice(0, limit) : list.filter((item) => item !== id);
  const record = async () => {
    const committed = await onRecord({
      kind,
      summary: summary.trim(),
      rationale: rationale.trim(),
      inputRecordIds: inputs,
      causedByEventIds: causes,
      evidence: availableEvidence.filter((item) => evidence.includes(evidenceKey(item))),
    });
    if (committed) {
      setSummary("");
      setRationale("");
      setInputs([]);
      setCauses([]);
      setEvidence([]);
    }
  };
  return (
    <section
      aria-label={t("Record reasoning step")}
      className="flex flex-col gap-3 rounded border border-divider p-3"
    >
      <h4 className="font-semibold">{t("Control the next recorded step")}</h4>
      <p className="text-small text-text-secondary">
        {t(
          "Describe an observed interpretation, alternative, decision or verification. This creates an actor report with exact inputs and causes; it does not reveal hidden model thoughts.",
        )}
      </p>
      <label>
        {t("Step type")}{" "}
        <select aria-label={t("Step type")} value={kind} onChange={(e) => setKind(e.target.value)}>
          {STEP_KINDS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t("What happened?")}{" "}
        <textarea
          aria-label={t("Step summary")}
          maxLength={4000}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className="w-full rounded border border-divider p-2"
        />
      </label>
      <label>
        {t("Recorded rationale (optional)")}{" "}
        <textarea
          aria-label={t("Step rationale")}
          maxLength={4000}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          className="w-full rounded border border-divider p-2"
        />
      </label>
      <details>
        <summary>{t("Input records ({{n}})", { n: inputs.length })}</summary>
        <div className="max-h-44 overflow-auto">
          {session.records.map((row) => (
            <label key={row.id} className="block">
              <input
                type="checkbox"
                checked={inputs.includes(row.id)}
                onChange={(e) => setInputs((ids) => toggle(ids, row.id, e.target.checked))}
              />
              {row.kind} · {row.logicalId} · r{row.revision}
            </label>
          ))}
        </div>
      </details>
      <details>
        <summary>{t("Causing events ({{n}})", { n: causes.length })}</summary>
        <div className="max-h-44 overflow-auto">
          {events.slice(-PICK_LIMIT).map((event) => (
            <label key={event.id} className="block">
              <input
                type="checkbox"
                checked={causes.includes(event.id)}
                onChange={(e) => setCauses((ids) => toggle(ids, event.id, e.target.checked))}
              />
              {event.sequence}. {event.kind}
            </label>
          ))}
        </div>
      </details>
      <details>
        <summary>{t("Pinned evidence ({{n}})", { n: evidence.length })}</summary>
        <div className="max-h-44 overflow-auto">
          {availableEvidence.map((item) => (
            <label key={evidenceKey(item)} className="block">
              <input
                type="checkbox"
                checked={evidence.includes(evidenceKey(item))}
                onChange={(e) =>
                  setEvidence((keys) =>
                    toggle(keys, evidenceKey(item), e.target.checked, Number.MAX_SAFE_INTEGER),
                  )
                }
              />
              {item.sourceKind}:{item.sourceId} · {item.revision}
            </label>
          ))}
        </div>
      </details>
      <Button disabled={disabled || !summary.trim()} onClick={() => void record()}>
        {t("Record step")}
      </Button>
    </section>
  );
}
